import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { organizationAPI } from '../../api';
import { useToast } from '../../contexts/ToastContext';
import { copyToClipboard } from '../../utils/clipboard';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const defaultConfig = { enabled: false, api_key: false, webhook_secret: false };

const TldvConfigTab = ({ orgId, org }) => {
  const toast = useToast();
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [form, setForm] = useState({ enabled: false, api_key: '', webhook_secret: '' });

  const webhookUrl = org?.slug ? `${API_BASE_URL}/api/v1/webhooks/tldv/${org.slug}` : '';

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationAPI.getTldvConfig(orgId);
      setConfig(data);
      setForm((prev) => ({ ...prev, enabled: data.enabled }));
    } catch {
      toast.error('Failed to load tl;dv configuration');
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { enabled: form.enabled };
      if (form.api_key) payload.api_key = form.api_key;
      if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;
      const updated = await organizationAPI.updateTldvConfig(orgId, payload);
      setConfig(updated);
      setForm((prev) => ({ ...prev, api_key: '', webhook_secret: '' }));
      toast.success('tl;dv configuration saved');
    } catch {
      toast.error('Failed to save tl;dv configuration');
    } finally {
      setSaving(false);
    }
  };

  // The enable toggle persists immediately (no Save click needed).
  const handleToggleEnabled = async () => {
    const next = !form.enabled;
    setForm((f) => ({ ...f, enabled: next })); // optimistic
    setTogglingEnabled(true);
    try {
      const updated = await organizationAPI.updateTldvConfig(orgId, { enabled: next });
      setConfig(updated);
      setForm((f) => ({ ...f, enabled: updated.enabled }));
      toast.success(next ? 'tl;dv enabled' : 'tl;dv disabled');
    } catch {
      setForm((f) => ({ ...f, enabled: !next })); // revert on failure
      toast.error('Failed to update tl;dv');
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleCopyWebhook = async () => {
    const ok = await copyToClipboard(webhookUrl);
    toast[ok ? 'success' : 'error'](ok ? 'Webhook URL copied' : 'Failed to copy');
  };

  if (loading) {
    return (
      <Card>
        <CardBody><p className="text-center text-gray-500 dark:text-gray-400 py-4">Loading…</p></CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">tl;dv</h2>
      </CardHeader>
      <CardBody className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white">Enable integration</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When disabled, webhook requests to this org's URL are silently dropped.
            </p>
          </div>
          <button
            type="button"
            onClick={handleToggleEnabled}
            disabled={togglingEnabled}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${form.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {form.enabled && (
          <>
            <hr className="border-gray-200 dark:border-gray-700" />

            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Inbound Webhook URL</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Point this org's tl;dv webhook at this URL so transcripts route into this organization.
              </p>
              <div className="flex gap-2 items-center">
                <code className="flex-1 min-w-0 text-xs bg-gray-100 dark:bg-gray-800 rounded px-3 py-2 break-all select-all">{webhookUrl}</code>
                <Button size="sm" variant="secondary" onClick={handleCopyWebhook} className="shrink-0">Copy</Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                tl;dv API Key
                {config.api_key && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>}
              </label>
              <Input
                type="password"
                placeholder={config.api_key ? 'Leave blank to keep existing key' : 'Enter API key'}
                value={form.api_key}
                onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Webhook Secret (x-api-key)
                {config.webhook_secret && <span className="ml-2 text-xs text-green-600 dark:text-green-400">(configured)</span>}
              </label>
              <Input
                type="password"
                placeholder={config.webhook_secret ? 'Leave blank to keep existing secret' : 'Enter webhook secret'}
                value={form.webhook_secret}
                onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))}
                autoComplete="new-password"
              />
              {!config.webhook_secret && (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  Required — webhook requests will be rejected (401) until a secret is set.
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving || !form.enabled}>{saving ? 'Saving…' : 'Save Configuration'}</Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default TldvConfigTab;
